import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { intermediaryGetIntermediaryFcaData, FcaResponseResult, IntermediaryFcaData } from '@pmp/api';
import { notification } from '@ohpen/enso-ui';

import { TabProps } from '../interfaces';
import { showFcaNotification } from '../../firms/utils';
import { PersonalDetailsTab } from './PersonalDetailsTab';
import { ContactDetailsTab } from './ContactDetailsTab';
import { StatusTab } from './StatusTab';
import { TabContent } from './TabContent';

export const BrokerDetailsTab: React.FC<TabProps> = ({ form, mode, selectsOptions }) => {
  const { t } = useTranslation();
  const [fcaModifiedFields, setFcaModifiedFields] = useState<IntermediaryFcaData | null | undefined>(null);

  const populateData = useCallback(
    (irn: string): Promise<void> => {
      return intermediaryGetIntermediaryFcaData({ path: { irn } })
        .then(({ data }) => {
          const { result, registrationDetails, personalDetails } = data;
          if (!result) return;

          if (result === FcaResponseResult.OK || result === FcaResponseResult.PARTIAL_DATA) {
            form?.setFieldsValue({
              irn: registrationDetails?.irn,
              fcaStatus: registrationDetails?.fcaStatus,
              personalData: {
                ...form?.getFieldValue('personalData'),
                firstName: personalDetails?.firstName,
                middleName: personalDetails?.middleName,
                lastName: personalDetails?.lastName,
                preferredName: personalDetails?.preferredName,
              },
            });
            setFcaModifiedFields(data);
          }

          showFcaNotification(result, t, notification());
        })
        .catch(() => {
          notification().error({ message: t('global.errors.unexpected') });
        });
    },
    [form, t],
  );

  const removeFcaModifiedField = useCallback((key: keyof IntermediaryFcaData, nestedKey?: string) => {
    setFcaModifiedFields(prev => {
      const next = { ...prev };
      if (nestedKey && next[key]) {
        const nested = { ...(next[key] as Record<string, unknown>) };
        delete nested[nestedKey];
        next[key] = nested as never;
      } else {
        delete next[key];
      }
      return next;
    });
  }, []);

  return (
    <TabContent>
      <PersonalDetailsTab
        form={form}
        mode={mode}
        selectsOptions={selectsOptions}
        fcaModifiedFields={fcaModifiedFields}
        populateFcaData={populateData}
        removeFcaModifiedField={removeFcaModifiedField}
      />
      <ContactDetailsTab form={form} mode={mode} selectsOptions={selectsOptions} />
      <StatusTab form={form} mode={mode} selectsOptions={selectsOptions} />
    </TabContent>
  );
};
